import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  FileText, Video, BookOpen, FileSpreadsheet, Search, 
  Clock, Eye, ArrowRight, Filter, X, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'article', label: 'Articles', icon: FileText },
  { value: 'video', label: 'Videos', icon: Video },
  { value: 'guide', label: 'Guides', icon: BookOpen },
  { value: 'worksheet', label: 'Worksheets', icon: FileSpreadsheet },
];

function ContentLibrary() {
  const { user, logout } = useAuth();
  const [content, setContent] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState({ fearClusters: [], archetypes: [], topics: [] });
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fearFilter, setFearFilter] = useState('');
  const [archetypeFilter, setArchetypeFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTags();
    fetchRecommended();
  }, []);

  useEffect(() => {
    fetchContent();
  }, [typeFilter, fearFilter, archetypeFilter, topicFilter]);

  const fetchTags = async () => {
    try {
      const response = await axios.get('/api/admin/content/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchRecommended = async () => {
    try {
      const response = await axios.get('/api/content/recommended');
      setRecommended(response.data.content);
    } catch (error) {
      console.error('Failed to fetch recommended:', error);
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter && typeFilter !== 'all') params.append('contentType', typeFilter);
      if (fearFilter) params.append('fearCluster', fearFilter);
      if (archetypeFilter) params.append('archetype', archetypeFilter);
      if (topicFilter) params.append('topic', topicFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`/api/content?${params.toString()}`);
      setContent(response.data.content);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContent();
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setFearFilter('');
    setArchetypeFilter('');
    setTopicFilter('');
    setSearchQuery('');
  };

  const hasActiveFilters = typeFilter !== 'all' || fearFilter || archetypeFilter || topicFilter || searchQuery;

  const getContentIcon = (type) => {
    const typeConfig = CONTENT_TYPES.find(t => t.value === type);
    return typeConfig?.icon || FileText;
  };

  const ContentCard = ({ item, isRecommended = false }) => {
    const Icon = getContentIcon(item.contentType);
    
    return (
      <Card 
        className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
        onClick={() => window.location.href = `/library/${item.id}`}
        data-testid={`content-card-${item.id}`}
      >
        {item.thumbnailUrl ? (
          <div className="aspect-video bg-muted overflow-hidden">
            <img 
              src={item.thumbnailUrl} 
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ) : (
          <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
            <Icon className="w-12 h-12 text-primary/50" />
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground capitalize">{item.contentType}</span>
            {isRecommended && (
              <Badge variant="secondary" className="text-xs ml-auto">
                <Sparkles className="w-3 h-3 mr-1" />
                For You
              </Badge>
            )}
          </div>
          
          <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {item.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {item.viewCount || 0}
              </span>
              {item.readTime && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {item.readTime}
                </span>
              )}
              {item.duration && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {item.duration}
                </span>
              )}
            </div>
            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const FilterPanel = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Content Type</label>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Edge Pattern</label>
        <Select value={fearFilter || 'none'} onValueChange={(v) => setFearFilter(v === 'none' ? '' : v)}>
          <SelectTrigger>
            <SelectValue placeholder="All patterns" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">All patterns</SelectItem>
            {tags.fearClusters.map(tag => (
              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Core Archetype</label>
        <Select value={archetypeFilter || 'none'} onValueChange={(v) => setArchetypeFilter(v === 'none' ? '' : v)}>
          <SelectTrigger>
            <SelectValue placeholder="All archetypes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">All archetypes</SelectItem>
            {tags.archetypes.map(tag => (
              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Topic</label>
        <Select value={topicFilter || 'none'} onValueChange={(v) => setTopicFilter(v === 'none' ? '' : v)}>
          <SelectTrigger>
            <SelectValue placeholder="All topics" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">All topics</SelectItem>
            {tags.topics.map(tag => (
              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {hasActiveFilters && (
        <Button variant="outline" className="w-full" onClick={clearFilters}>
          <X className="w-4 h-4 mr-2" />
          Clear Filters
        </Button>
      )}
    </div>
  );

  return (
    <div data-testid="content-library">
      {/* Hero Section */}
      <div className="mb-8">
        <h1 className="h1 text-3xl mb-2">Resource Library</h1>
        <p className="text-muted-foreground text-lg">
          Articles, videos, and guides to support your leadership development journey
        </p>
      </div>

      {/* Recommended Section */}
      {recommended.length > 0 && !hasActiveFilters && (
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-lg">Recommended for You</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommended.slice(0, 4).map(item => (
                <ContentCard key={item.id} item={item} isRecommended />
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
              data-testid="library-search-input"
            />
            <Button type="submit" variant="outline">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          
          {/* Desktop Filters */}
          <div className="hidden lg:flex gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={fearFilter || 'none'} onValueChange={(v) => setFearFilter(v === 'none' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Edge Pattern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All patterns</SelectItem>
                {tags.fearClusters.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={archetypeFilter || 'none'} onValueChange={(v) => setArchetypeFilter(v === 'none' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Core Archetype" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All archetypes</SelectItem>
                {tags.archetypes.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Mobile Filter Button */}
          <Sheet open={showFilters} onOpenChange={setShowFilters}>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden">
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">Active</Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>Filter Resources</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterPanel />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {total} resource{total !== 1 ? 's' : ''} found
          </p>
          {hasActiveFilters && (
            <div className="flex gap-2 flex-wrap">
              {typeFilter !== 'all' && (
                <Badge variant="secondary">
                  {CONTENT_TYPES.find(t => t.value === typeFilter)?.label}
                </Badge>
              )}
              {fearFilter && <Badge variant="secondary">{fearFilter}</Badge>}
              {archetypeFilter && <Badge variant="secondary">{archetypeFilter}</Badge>}
              {topicFilter && <Badge variant="secondary">{topicFilter}</Badge>}
            </div>
          )}
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : content.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No resources found</h3>
            <p className="text-muted-foreground mb-4">Try adjusting your filters or search query</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {content.map(item => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        )}
    </div>
  );
}

export default ContentLibrary;
